import { inject, Injectable } from '@angular/core';
import { FeatureOperationServiceModule } from '@data-terminal/feature-workstation-details/operation-service.module';
import { OperationApiService } from '@data-terminal/feature-workstation-details/services/operation-api/operation-api.service';
import { ClientApiError } from '@data-terminal/shared-models/client-api-error';
import { IOTResponseError } from '@data-terminal/shared-models/datatransfer';
import {
    CreateOperation,
    InputStackOccupancy,
    Operation,
    OperationActionRequest,
    SetCommentParam,
    StackOccupancyScope,
    StartOperationActivity,
} from '@data-terminal/shared-models/operation';
import { StatusActionResponse } from '@data-terminal/shared-models/status-action-response';
import { RETRY_TIME_DELAY_MS } from '@data-terminal/utils/constants';
import { mapToRequestMetadataWithRetry, RequestMetadata } from '@data-terminal/utils/helpers';
import { Observable, share } from 'rxjs';

@Injectable({
    providedIn: FeatureOperationServiceModule,
})
export class OperationService {
    private readonly operationApi = inject(OperationApiService);

    public getOperationRequestMetadata(primaryKey: string): Observable<RequestMetadata<Operation>> {
        return this.operationApi.getOperation(primaryKey).pipe(
            mapToRequestMetadataWithRetry({
                baseDelayMs: RETRY_TIME_DELAY_MS,
            })
        );
    }

    public getOperation(primaryKey: string): Observable<Operation> {
        return this.operationApi.getOperation(primaryKey);
    }

    public getOperationStorageOccupancies(
        primaryKey: string,
        scope: StackOccupancyScope
    ): Observable<InputStackOccupancy[]> {
        return this.operationApi.getOperationStorageOccupancies(primaryKey, scope);
    }

    public getOperationList(machineId: string): Observable<Operation[]> {
        return this.operationApi.getOperationList(machineId).pipe(share());
    }

    public getRunningOperations(machineId: string): Observable<Operation[]> {
        return this.operationApi.getRunningOperations(machineId).pipe(share());
    }

    public checkOperationListUpdate(machineId: string, timestamp: string): Observable<boolean> {
        return this.operationApi.checkOperationListUpdate(machineId, timestamp);
    }

    public startActivity(startOperationActivity: StartOperationActivity): Observable<Operation | ClientApiError> {
        return this.operationApi.startOperationActivity(startOperationActivity);
    }

    public startActivityWithMessageIds(
        startOperationActivity: StartOperationActivity
    ): Observable<StatusActionResponse<Operation> | ClientApiError> {
        return this.operationApi.startOperationActivityWithMessageIds(startOperationActivity);
    }

    public updateOperationAction(
        operationActionRequest: OperationActionRequest
    ): Observable<Operation | ClientApiError> {
        return this.operationApi.updateOperationAction(operationActionRequest);
    }

    public updateOperationActionWithMessageIds(
        operationActionRequest: OperationActionRequest
    ): Observable<StatusActionResponse<Operation> | ClientApiError> {
        return this.operationApi.updateOperationActionWithMessageIds(operationActionRequest);
    }

    public createNewOperation(
        createOperation: CreateOperation
    ): Observable<RequestMetadata<Operation | IOTResponseError | ClientApiError>> {
        return this.operationApi.createNewOperation(createOperation).pipe(
            mapToRequestMetadataWithRetry({
                baseDelayMs: RETRY_TIME_DELAY_MS,
            })
        );
    }

    public getOrderBag(primaryKey: string, machineId: string): Observable<string> {
        return this.operationApi.getOrderBag(primaryKey, machineId);
    }

    public printPalletSheet(primaryKey: string, machineId: string, shouldRequestPickup: boolean): Observable<boolean> {
        return this.operationApi.printPalletSheet(primaryKey, machineId, shouldRequestPickup);
    }

    public completedOperationlist(machineId: string, jobId: string): Observable<Operation[]> {
        return this.operationApi.completedOperationlist(machineId, jobId);
    }

    public operationlistByClass(machineId: string, jobId: string): Observable<Operation[]> {
        return this.operationApi.operationlistByClass(machineId, jobId);
    }

    public operationlistByJobId(jobId: string): Observable<Operation[]> {
        return this.operationApi.operationlistByJobId(jobId);
    }

    public getComments(workstep: string): Observable<string[]> {
        return this.operationApi.getComment(workstep);
    }

    public setComment(commentParam: SetCommentParam): Observable<boolean> {
        return this.operationApi.setComment(commentParam);
    }
}
